import React, { useEffect, useId, useRef, useState } from 'react';
import { buildAutomationChildTestId, resolveAutomationTestId } from './testId';

const InlineHelpTooltip = ({
  content,
  ariaLabel = "More information",
  align = "left",
  className = "",
  testId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);
  const tooltipId = useId();
  const resolvedTestId = resolveAutomationTestId(testId);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  if (!content) {
    return null;
  }

  return (
    <span
      ref={wrapperRef}
      className={`rw-inline-tooltip ${className}`.trim()}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
      data-testid={resolvedTestId}
    >
      <button
        type="button"
        className={`rw-inline-tooltip__trigger ${isOpen ? "is-open" : ""}`}
        aria-label={ariaLabel}
        aria-describedby={tooltipId}
        aria-expanded={isOpen}
        data-testid={buildAutomationChildTestId(resolvedTestId, "trigger")}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setIsOpen((prev) => !prev);
        }}
        onFocus={() => setIsOpen(true)}
        onBlur={(event) => {
          if (!wrapperRef.current?.contains(event.relatedTarget)) {
            setIsOpen(false);
          }
        }}
      >
        <span className="rw-inline-tooltip__icon">i</span>
      </button>
      <span
        id={tooltipId}
        role="tooltip"
        className={`rw-inline-tooltip__content rw-inline-tooltip__content--${align} ${isOpen ? "is-visible" : ""}`}
        data-testid={buildAutomationChildTestId(resolvedTestId, "content")}
      >
        {content}
      </span>
    </span>
  );
};

export default InlineHelpTooltip;
